/**
 * The values most recently used for a field, newest first.
 *
 * What a suggestion list cannot say: a field's options are every value the
 * project has ever used, deduped and sorted alphabetically — the right list to
 * SEARCH and the wrong one to REPEAT. Entering forty photos off one film,
 * "Filmnummer 12" is the value from a moment ago, not one among thirty.
 *
 * Ordered by `updatedAt`, not `createdAt`: what counts is when a value was last
 * PUT there. Sorting on the record rather than on the grid keeps a sort order
 * or a filter from changing what "last" means, which the field app and the
 * office have to agree on.
 *
 * Project-wide and not per person: the other person's Filmnummer is as likely
 * to be the wanted one as your own.
 */

/** How many values a field offers back. Beyond this nobody scans a list. */
export const RECENT_VALUES_LIMIT = 8;

export interface RecentValueRow {
    updatedAt?: string;
    createdAt?: string;
    [field: string]: unknown;
}

/**
 * One field's value as a person typed it — a list field ', '-joined, exactly
 * the text its cell and its chips show. Anything else comes back empty.
 */
function valueText(raw: unknown): string {
    if (Array.isArray(raw)) {
        return raw
            .map((entry) => String(entry ?? '').trim())
            .filter(Boolean)
            .join(', ');
    }
    return typeof raw === 'string' ? raw.trim() : '';
}

/** When a record was last written — `createdAt` while nothing has updated it. */
function writtenAt(row: RecentValueRow): string {
    return row.updatedAt || row.createdAt || '';
}

/**
 * The last {@link RECENT_VALUES_LIMIT} distinct values of `field`, newest
 * first. Deduped keeping the FIRST occurrence, so a value used again today
 * sits at the top. Empty values are left out: "nothing" is not something
 * anyone asks to repeat.
 */
export function recentValues(
    rows: readonly RecentValueRow[],
    field: string,
    limit: number = RECENT_VALUES_LIMIT
): string[] {
    const newestFirst = [...rows].sort((firstRow, secondRow) =>
        writtenAt(secondRow).localeCompare(writtenAt(firstRow))
    );
    const seen = new Set<string>();
    for (const row of newestFirst) {
        const value = valueText(row[field]);
        if (value && !seen.has(value)) {
            seen.add(value);
            if (seen.size >= limit) {
                break;
            }
        }
    }
    return [...seen];
}

/**
 * Hang each field's recent values off its spec. Structurally typed, so it
 * serves the grid's `InlineField` list and any other shape carrying a `field`
 * name.
 *
 * `except` is for the fields where repeating is a MISTAKE: a Bildnummer counts
 * up, and offering the last one is offering a duplicate.
 */
export function withRecentValues<F extends { field: string }>(
    fields: readonly F[],
    rows: readonly RecentValueRow[],
    except: readonly string[] = []
): (F & { recent?: string[] })[] {
    return fields.map((spec) =>
        except.includes(spec.field)
            ? { ...spec }
            : { ...spec, recent: recentValues(rows, spec.field) }
    );
}
